import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useContext } from '@/hooks';
import {
  Menu,
  ChevronDown,
  Building2,
  Stethoscope,
  LogOut,
  User,
  Settings
} from 'lucide-react';

interface HeaderProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export function Header({ onMenuClick, showMenuButton = true }: HeaderProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { clinic, doctor, clearContext } = useContext();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
    clearContext();
    navigate('/login');
  };

  const handleContextChange = () => {
    navigate('/clinicas');
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side */}
          <div className="flex items-center">
            {showMenuButton && (
              <button
                onClick={onMenuClick}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 lg:hidden"
              >
                <Menu className="h-6 w-6" />
              </button>
            )}

            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">
                Gestão Clínica
              </h1>
            </div>
          </div>

          {/* Center - Context Display */}
          {(clinic || doctor) && (
            <div className="hidden md:flex items-center space-x-4">
              {clinic && (
                <button
                  onClick={handleContextChange}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    {clinic.name}
                  </span>
                </button>
              )}

              {doctor && (
                <button
                  onClick={() => navigate('/medicos')}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Stethoscope className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    Dr(a). {doctor.name}
                  </span>
                </button>
              )}
            </div>
          )}

          {/* Right side - User Menu */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-700">
                      {user?.name || user?.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.role}
                    </p>
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </button>

              {/* User Dropdown */}
              {showUserMenu && (
                <>
                  <button
                    className="fixed inset-0 z-40"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/perfil');
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <User className="h-4 w-4" />
                      <span>Meu Perfil</span>
                    </button>

                    {user?.role === 'Administrador' && (
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/admin/usuarios');
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                      >
                        <Settings className="h-4 w-4" />
                        <span>Configurações</span>
                      </button>
                    )}

                    <hr className="my-1 border-gray-200" />

                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        handleLogout();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Sair</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Context Display */}
        {(clinic || doctor) && (
          <div className="md:hidden pb-3 border-t border-gray-200 mt-2 pt-3">
            <div className="flex items-center justify-center space-x-4">
              {clinic && (
                <button
                  onClick={handleContextChange}
                  className="flex items-center space-x-1 text-xs px-2 py-1 rounded-md hover:bg-gray-50"
                >
                  <Building2 className="h-3 w-3 text-gray-500" />
                  <span className="text-gray-700">{clinic.name}</span>
                </button>
              )}

              {doctor && (
                <button
                  onClick={() => navigate('/medicos')}
                  className="flex items-center space-x-1 text-xs px-2 py-1 rounded-md hover:bg-gray-50"
                >
                  <Stethoscope className="h-3 w-3 text-gray-500" />
                  <span className="text-gray-700">Dr. {doctor.name}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}